import {
  adminArchive,
  adminArticles,
  adminBeneficialOwners,
  adminDashboard,
  adminFollowUp,
  adminMessaging,
  adminOverview,
  adminOwnership,
  adminPoA,
  adminProjects,
  adminRoles,
  adminSettings,
  adminShareholders,
  adminShareholdersAltinn,
  adminShareholdersList,
  adminShareholdersShareClasses,
  adminShareholdersShareholderCommitments,
  adminShareholdersShareholderContributions,
  adminShareholdersTransactions,
  boardMemberArchive,
  boardMemberMessaging,
  boardMemberOverview,
  boardMemberOverviewArticles,
  scheduleUrl,
  shareholderArchive,
  shareholderMessaging,
  shareholderOverview,
  shareholderOverviewArticles,
} from "../utils/navigation";

import { IconName } from "../components-v2/iconMapping";
import {
  canRenderAdminMeetings,
  canRenderAdminProjects,
  canRenderArchive,
  canRenderMessages,
} from "../payment/ApplicationFeatures";
import { compact, trimEnd } from "lodash";
import { TMessage } from "../i18n/tt";
import { isCountryCodeNorway } from "../utils/countries";
import { AccessType } from "../types/enums/AccessType";
import { isProductionEnvironment } from "../utils/environments";

import { PaidSubscription } from "../common/subscription";
import { CustomRoleForUser } from "../common/customRole";
import { toQueryString } from "../utils/queryParams";
import { shareholderCommitmentWhitelist } from "../common/featureToggles";
import { BetaLabelProps } from "../components-v2/BetaLabel";
import { isContributionsEnabled } from "../shareholderRegister/contributions/shareholderContributionsDomain";

export type HeaderLink = {
  icon: IconName;
  to: string;
  text: TMessage;
  basePath: string;
  exact?: boolean;
  badge?: TMessage;
  badgeColor?: BetaLabelProps["color"];
  subLinks?: SubLink[];
};

export type SubLink = {
  to: string;
  text: TMessage;
  exact?: boolean;
  badge?: TMessage;
  tag?: TMessage;
  badgeColor?: BetaLabelProps["color"];
};

export const adminLinks = ({
  orgNo,
  country,
  subscription,
  userRole,
  roleId,
  followUpCount,
}: {
  orgNo: string;
  country?: string;
  subscription?: PaidSubscription;
  userRole?: CustomRoleForUser;
  roleId?: string;
  followUpCount?: number;
}): HeaderLink[] => {
  const noRole = userRole === undefined || userRole.customRoleId === "all";
  const queryParam = toQueryString({ roleId });
  const shareholdersSubLinks: SubLink[] = compact([
    (noRole || userRole?.allowShareholdersView || userRole?.allowShareholdersEdit) && {
      to: `${adminShareholdersList(orgNo)}${queryParam}`,
      text: { key: "header.shareholders" },
    },
    (noRole || userRole?.allowTransactionsView || userRole?.allowTransactionsEdit) && {
      to: `${adminShareholdersTransactions(orgNo)}${queryParam}`,
      text: { key: "header.transactions" },
    },
    (noRole || userRole?.allowShareClassView || userRole?.allowShareClassEdit) && {
      to: `${adminShareholdersShareClasses(orgNo)}${queryParam}`,
      text: { key: "header.stock-classes" },
    },
  ]);
  if (isContributionsEnabled(orgNo)) {
    shareholdersSubLinks.push({
      to: `${adminShareholdersShareholderContributions(orgNo)}${queryParam}`,
      text: { key: "header.shareholder-contributions" },
    });
  }

  if (
    isCountryCodeNorway(country) &&
    (!isProductionEnvironment() || shareholderCommitmentWhitelist.includes(orgNo))
  ) {
    if (noRole || userRole?.allowCommitmentsEdit || userRole?.allowCommitmentsView) {
      shareholdersSubLinks.push({
        to: `${adminShareholdersShareholderCommitments(orgNo)}${queryParam}`,
        text: { key: "header.shareholder-commitments" },
      });
    }
  }
  if (
    !country ||
    (isCountryCodeNorway(country) &&
      (noRole ||
        userRole?.allowShareholderRegisterStatementView ||
        userRole?.allowShareholderRegisterStatementEdit))
  ) {
    shareholdersSubLinks.push({
      to: `${adminShareholdersAltinn(orgNo)}${queryParam}`,
      text: { key: "header.altinn" },
    });
  }
  if (
    isCountryCodeNorway(country) &&
    (noRole || userRole?.allowBeneficialOwnersView || userRole?.allowBeneficialOwnersEdit)
  ) {
    shareholdersSubLinks.push({
      to: `${adminBeneficialOwners(orgNo)}${queryParam}`,
      text: { key: "header.beneficial-owners" },
    });
  }
  const adminProjectsLink: HeaderLink = {
    icon: "sparkle",
    basePath: adminProjects(orgNo),
    to: `${adminProjects(orgNo)}${queryParam}`,
    text: { key: "header.dbot" },
  };
  const adminScheduleLink: HeaderLink = {
    icon: "calendar",
    basePath: scheduleUrl({ accessType: AccessType.ADMIN, orgNo }),
    to: `${scheduleUrl({ accessType: AccessType.ADMIN, orgNo })}${queryParam}`,
    text: { key: "header.schedule" },
  };

  const archiveLink: HeaderLink = {
    icon: "archive",
    basePath: adminArchive({ orgNo }),
    to: `${adminArchive({ orgNo })}${queryParam}`,
    text: { key: "header.archive" },
  };
  const messagesLink: HeaderLink = {
    icon: "envelope",
    basePath: adminMessaging(orgNo),
    to: `${adminMessaging(orgNo)}${queryParam}`,
    text: { key: "header.messaging" },
  };
  const overviewLink: HeaderLink = {
    icon: "buildings",
    to: `${adminOverview(orgNo)}${queryParam}`,
    text: { key: "header.company" },
    basePath: adminOverview(orgNo),
    exact: true,
    subLinks: compact([
      (noRole || userRole.allowDashboardView) && {
        to: `${adminDashboard(orgNo)}${queryParam}`,
        text: { key: "header.dashboard" },
        exact: true,
      },
      (noRole || userRole.allowOverview) && {
        to: `${adminFollowUp(orgNo)}${queryParam}`,
        text: { key: "header.follow-up" },
        tag: followUpCount ? { translated: String(followUpCount) } : undefined,
      },
      (noRole || userRole?.allowArticlesEdit || userRole?.allowArticlesView) && {
        to: `${adminArticles(orgNo)}${queryParam}`,
        text: { key: "header.articles-of-association" },
      },
      (noRole || userRole?.allowPoAsEdit || userRole?.allowPoAsView) && {
        to: `${adminPoA(orgNo)}${queryParam}`,
        text: { key: "header.poa" },
      },
      (noRole || userRole?.allowOwnershipView) && {
        to: `${adminOwnership(orgNo)}${queryParam}`,
        text: { key: "header.ownership" },
      },
      (noRole || userRole?.allowCompanyRolesEdit || userRole?.allowCompanyRolesView) && {
        to: `${adminRoles(orgNo)}${queryParam}`,
        text: { key: "companyRoles.header" },
      },
      (noRole || userRole?.allowCompanySettingsView || userRole?.allowCompanySettingsEdit) && {
        to: `${adminSettings(orgNo)}${queryParam}`,
        text: { key: "settings" },
      },
    ]),
  };
  const shareholderRegisterLink: HeaderLink = {
    icon: "notebook",
    basePath: adminShareholders(orgNo),
    to: `${adminShareholdersList(orgNo)}${queryParam}`,
    text: { key: "header.shareholderBook" },
    subLinks: shareholdersSubLinks,
  };
  return compact([
    (noRole || userRole?.allowOverview) && overviewLink,
    (noRole || userRole.allowShareholderRegister) && shareholderRegisterLink,
    (noRole || userRole.allowDbot) && canRenderAdminProjects(subscription) && adminProjectsLink,
    (noRole || userRole?.allowDbot) && canRenderAdminMeetings(subscription) && adminScheduleLink,
    (noRole || userRole.allowArchive) && canRenderArchive(subscription) && archiveLink,
    (noRole || userRole.allowMessaging) && canRenderMessages(subscription) && messagesLink,
  ]);
};

export const shareholderLinks = (orgNumber: string, shareholderId: number): HeaderLink[] => [
  {
    icon: "buildings",
    to: shareholderOverview(orgNumber, shareholderId),
    text: { key: "header.company" },
    basePath: shareholderOverview(orgNumber, shareholderId),
    exact: true,
    subLinks: compact([
      {
        to: shareholderOverview(orgNumber, shareholderId, "dashboard"),
        text: { key: "header.dashboard" },
        exact: true,
      },
      {
        to: shareholderOverview(orgNumber, shareholderId, "overview"),
        text: { key: "shareholder-details.shareholding" },
        exact: true,
      },
      {
        to: shareholderOverview(orgNumber, shareholderId, "transactions"),
        text: { key: "shareholder-details.transactions" },
        exact: true,
      },
      {
        to: shareholderOverview(orgNumber, shareholderId, "details"),
        text: { key: "shareholders.details" },
        exact: true,
      },
      shareholderCommitmentWhitelist.includes(orgNumber) && {
        to: shareholderOverview(orgNumber, shareholderId, "commitments"),
        text: { key: "shareholder-commitments.commitments.heading" },
        exact: true,
      },
      isContributionsEnabled(orgNumber) && {
        to: shareholderOverview(orgNumber, shareholderId, "contributions"),
        text: { key: "shareholder-contributions.contributions.heading" },
        exact: true,
      },
      {
        to: shareholderOverview(orgNumber, shareholderId, "additional-representatives"),
        text: { key: "additional-representatives" },
        exact: true,
      },
      {
        to: shareholderOverviewArticles(orgNumber, shareholderId),
        text: { key: "header.articles-of-association" },
        exact: true,
      },
    ]),
  },
  {
    icon: "calendar",
    to: scheduleUrl({ accessType: AccessType.SHAREHOLDER, orgNo: orgNumber, shareholderId }),
    text: { key: "header.meetings" },
    basePath: scheduleUrl({ accessType: AccessType.SHAREHOLDER, orgNo: orgNumber, shareholderId }),
  },
  {
    icon: "archive",
    to: shareholderArchive(orgNumber, shareholderId),
    text: { key: "header.shareholderview.archive" },
    basePath: shareholderArchive(orgNumber, shareholderId),
  },
  {
    icon: "envelope",
    to: shareholderMessaging(orgNumber, shareholderId),
    text: { key: "header.recipient.messaging" },
    basePath: shareholderMessaging(orgNumber, shareholderId),
  },
];

export const boardMemberLinks = (orgNumber: string): HeaderLink[] => {
  return [
    {
      icon: "buildings",
      to: boardMemberOverview(orgNumber),
      text: { key: "header.company" },
      basePath: boardMemberOverview(orgNumber),
      exact: true,
      subLinks: [
        {
          to: boardMemberOverview(orgNumber),
          text: { key: "header.dashboard" },
          exact: true,
        },
        {
          to: boardMemberOverviewArticles(orgNumber),
          text: { key: "header.articles-of-association" },
          exact: true,
        },
      ],
    },
    {
      icon: "calendar",
      basePath: scheduleUrl({ accessType: AccessType.BOARD_MEMBER, orgNo: orgNumber }),
      to: scheduleUrl({ accessType: AccessType.BOARD_MEMBER, orgNo: orgNumber }),
      text: { key: "header.schedule" },
    },
    {
      icon: "envelope",
      to: boardMemberMessaging(orgNumber),
      text: { key: "header.recipient.messaging" },
      basePath: boardMemberMessaging(orgNumber),
    },
    {
      icon: "archive",
      to: boardMemberArchive({ orgNo: orgNumber }),
      text: { key: "header.archive" },
      basePath: boardMemberArchive({ orgNo: orgNumber }),
    },
  ];
};

export const findActiveTopMenuItem = (
  links: HeaderLink[],
  locationPathName: string,
): HeaderLink | undefined => {
  const match = links.find(
    (m) =>
      (m.basePath && m.basePath === locationPathName) ||
      m.subLinks?.some(
        (s) => trimEnd(s.to, "/") === trimEnd(locationPathName, "/") && s.exact === true,
      ),
  );
  if (match) {
    return match;
  } else {
    return links.find(
      (m) =>
        (m.basePath === locationPathName && m.exact === true) ||
        (!m.exact && locationPathName.startsWith(m.basePath)) ||
        m.subLinks?.some(({ to, exact }) => locationPathName.startsWith(to) && !exact),
    );
  }
};
